import React, { useCallback } from 'react';

import Checkbox from '../../../hoc/types/Checkbox';
import ColorInput from '../../../hoc/types/ColorInput';
import CssTextInput from '../../../hoc/types/CssTextInput';
import FontSelect from '../../../hoc/types/FontSelect';
import LegalInput from '../../../hoc/types/LegalInput';
import LengthSlider from '../../../hoc/types/LengthSlider';
import NumberSpinner from '../../../hoc/types/NumberSpinner';
import SelectInput from '../../../hoc/types/SelectInput';
import Slates from '../../../hoc/types/Slates';
import TextInput from '../../../hoc/types/TextInput';
import { DEFAULT_SWATCHES } from '../../../lib/themeTokens';

/**
 * Declarative input dispatcher for the PMM v4 player editor.
 * Replaces the legacy `PreferenceRenderUtil` HOC's imperative
 * `renderPreference()` call. Switches on `meta.inputType` and renders
 * the matching leaf component, reusing the existing inputs.
 *
 * Bridge note: leaf inputs (`Checkbox`, `TextInput`, etc.) call
 * `storeEdits(value)` with a single arg. The PMM v4 hook
 * (`useEditState`) takes `storeEdits(key, value)`. The dispatcher
 * binds `propertyKey` into a single-arg closure to match leaf
 * expectations without changing those components.
 *
 * @param {{
 *   propertyKey: string,
 *   meta: import('../../../lib/playerPreferenceMeta').MetaEntry,
 *   value: any,
 *   onChange: (propertyKey: string, value: any) => void,
 *   assets?: any[],
 * }} props
 */
export default function PreferenceInput ({ propertyKey, meta, value, onChange, assets, palette, fallback }) {
  const handleChange = useCallback(
    (val) => onChange(propertyKey, val),
    [propertyKey, onChange]
  );

  switch (meta.inputType) {
    case 'boolean':
      return (
        <Checkbox
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
        />
      );

    // A text field carrying CSS is a different control from one carrying
    // prose. `cssProperty` is what distinguishes them: a transcript notice or
    // a host URL has none and stays on the unscreened input, while a shorthand
    // headed for `setProperty()` gets screened, because the browser silently
    // discards what it cannot parse while `hasTheme()` still counts it.
    case 'text':
      if (meta.cssProperty) {
        return (
          <CssTextInput
            value={value}
            cssProperty={meta.cssProperty}
            placeholder={meta.placeholder || fallback}
            preferenceName={propertyKey}
            storeEdits={handleChange}
          />
        );
      }
      return (
        <TextInput
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
        />
      );

    case 'email':
      return (
        <TextInput
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
          type="email"
        />
      );

    case 'number':
      return (
        <NumberSpinner
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
          min={meta.min}
          max={meta.max}
        />
      );

    case 'select':
      return (
        <SelectInput
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
          options={meta.options}
        />
      );

    case 'legal':
      return (
        <LegalInput
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
        />
      );

    case 'assets':
      return (
        <Slates
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
          assets={assets}
        />
      );

    // Theme tokens. `fallback` is the player's own CSS default, shown
    // when the stored value is '' -- display only, never persisted.
    // `palette` is the colours this theme already uses, offered as swatches
    // so a two-colour brand is not re-typed six times.
    case 'color':
      return (
        <ColorInput
          value={value}
          fallback={fallback || DEFAULT_SWATCHES[propertyKey]}
          palette={palette}
          preferenceName={propertyKey}
          storeEdits={handleChange}
        />
      );

    case 'font':
      return (
        <FontSelect
          value={value}
          preferenceName={propertyKey}
          storeEdits={handleChange}
        />
      );

    case 'length':
      return (
        <LengthSlider
          value={value}
          fallback={fallback || DEFAULT_SWATCHES[propertyKey]}
          preferenceName={propertyKey}
          storeEdits={handleChange}
          min={meta.min}
          max={meta.max}
          step={meta.step}
          unit={meta.unit}
          cssProperty={meta.cssProperty}
          cssFormat={meta.cssFormat}
        />
      );

    default:
      return null;
  }
}
